import { useTranslation, Trans } from "react-i18next";
import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useForm } from "react-hook-form";
import moment from "moment";

import config from "config/config";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import MaskedExpirationDateInput from "components/General/Form/MaskedExpirationDateInput";
import UpdatePaymentProfile from "service/paymentServices/UpdatePaymentProfile";
import DialogBackdrop from "./DialogBackdrop";
import FormInputError from "../General/FormInputError";
import PaymentProfileConfirmationModel from "./PaymentProfileConfirmationModel";
const UpdateAutopayCCModal = ({
  modalState,
  setModalState,
  data,
  selectedPolicy,
  isUpdated,
  setIsUpdated,
}) => {
  const { t } = useTranslation();
  const [expireSoon, setExpireSoon] = useState(false);
  const [disableSave, setDisableSave] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [title, setTitle] = useState(false);
  const [message, setMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  useEffect(() => {
    setDisableSave(true);
    if (data?.cardStatus?.toLowerCase() !== "expired") {
      let firstdayExpireDate = moment(
        `${data?.expDate?.substring(0, 2)}/01/${data?.expDate?.substring(3)}`
      ).format("MM/DD/YYYY");
      let nextMonth = moment()
        .add(parseInt(config.CREDIT_CARD_EXPIRE_CHECK), "months")
        .format("MM/DD/YYYY");
      if (moment(firstdayExpireDate).month() === moment(nextMonth).month()) {
        setExpireSoon(true);
      } else {
        setExpireSoon(false);
      }
    }
  }, [data, isUpdated]);
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  const isValidExpireDate = () => {
    //months should be b/n 1 and 12
    if (
      parseInt(getValues("expirationDate").substring(0, 2)) <= 0 ||
      parseInt(getValues("expirationDate").substring(0, 2)) > 12
    )
      return false;
    //expire year should be >= current year
    if (moment().year() > getValues("expirationDate")?.substring(3))
      return false;
    // if the same year months should be > current month
    if (moment().year() === getValues("expirationDate")?.substring(3)) {
      if (
        moment().month() + 1 >=
        parseInt(getValues("expirationDate").substring(0, 2))
      )
        return false;
    }
    return true;
  };
  async function onSubmit(value) {
    if (!errors.expirationDate && getValues("expirationDate")) {
      if (isValidExpireDate()) {
        let policyDetails = {
          paymentProfileId: data?.paymentProfileId,
          cardExpiry: getValues("expirationDate")?.replace("/", ""),
          blockCode: selectedPolicy[0]?.blockCode,
        };
        setErrorMessage(undefined);
        setIsLoading(false);
        await UpdatePaymentProfile(policyDetails)
          .then((response) => {
            setMessage(
              t(
                "CustomerManagePayment-manage-payment-card-update-one-time-message1"
              )
            );
            setTitle(
              t(
                "CustomerManagePayment-manage-payment-card-update-one-time-title1"
              )
            );
            setModalState(false);
            setIsLoading(false);
            setOpenDialog(true);
            setDisableSave(true);
            setIsUpdated(!isUpdated);
          })
          .catch((error) => {
            console.error(
              "Server refused to connect, please try again later.",
              error
            );
            setMessage(
              t(
                "CustomerManagePayment-manage-payment-card-update-one-time-message2"
              )
            );
            setTitle(
              t(
                "CustomerManagePayment-manage-payment-card-update-one-time-title2"
              )
            );
            setModalState(false);
            setIsLoading(false);
            setOpenDialog(true);
          });
      } else {
        setDisableSave(true);
        setErrorMessage(
          t(
            "CustomerManagePayment-manage-payment-card-update-one-time-message2"
          )
        );
      }
    }
  }

  return (
    <>
      <Transition appear show={modalState} as={Fragment}>
        <Dialog
          className="fixed inset-0 z-50 overflow-y-auto"
          as="div"
          open={modalState}
          onClose={() => {
            setModalState(false);
            setErrorMessage(undefined);
            setDisableSave(true);
          }}
        >
          <DialogBackdrop />

          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0" />
            </Transition.Child>
            {/* This element is to trick the browser into centering the modal contents. */}

            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <Dialog.Title
                  as="h2"
                  className="dialog_label2 font-body text-xl font-bold text-left"
                >
                  {t(
                    "CustomerManagePayment-manage-payment-card-update-expiration-date"
                  )}
                  <button
                    className="ui-default-dialog-close"
                    onClick={() => {
                      setModalState(false);
                      setErrorMessage(undefined);
                      setDisableSave(true);
                    }}
                  >
                    <svg
                      className="ui-icon h-3.5 w-3.5"
                      focusable="false"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <use href="#ui-icon-close"></use>
                    </svg>
                  </button>
                </Dialog.Title>
                <hr />
                <EverlakeLoadingOverlay isLoading={isLoading}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <p className="font-body text-xl font-bold text-left mb-4">
                      {t("CustomerManagePayment-credit-card-information")}
                    </p>
                    <p className="mb-4">
                      {t("CustomerManagePayment-manage-payment-card-policies")}
                      {selectedPolicy?.length === 1 && (
                        <p>{selectedPolicy[0]?.oldPolicy}</p>
                      )}
                      {selectedPolicy?.length > 1 && (
                        <>
                          {selectedPolicy.map((sp) => (
                            <p>{sp?.oldPolicy}</p>
                          ))}
                        </>
                      )}
                    </p>
                    <div className="w-2/3">
                      <div>
                        <label id={`cardNumber-label`}>
                          {t("CustomerManagePayment-credit-card-number")}
                        </label>
                        <input
                          id={`cardNumber`}
                          disabled={true}
                          className="w-full"
                          type="text"
                          defaultValue={data?.accountNumber}
                        />
                      </div>
                    </div>
                    <div className="w-1/3">
                      <div className="my-4">
                        <label id={`expirationDate-label`}>
                          {t(
                            "CustomerManagePayment-manage-payment-card-update-expiration-date"
                          )}
                        </label>
                        <MaskedExpirationDateInput
                          name="expirationDate"
                          describedby={`expirationDate-label`}
                          register={register}
                          setValue={setValue}
                          defaultValue={data?.expDate}
                          setState={setDisableSave}
                        />
                        {errors.expirationDate && (
                          <FormInputError
                            id={`expirationDate-error`}
                            errorMessage={errors.expirationDate.message}
                          />
                        )}
                        {errorMessage && (
                          <FormInputError
                            id={`expirationDate-error`}
                            errorMessage={errorMessage}
                          />
                        )}
                      </div>
                    </div>
                    {!expireSoon && (
                      <div className="flex mt-4">
                        <p className="text-sm">
                          <Trans i18nKey="CustomerManagePayment-manage-payment-card-update-auto-pay-title1"></Trans>
                        </p>
                      </div>
                    )}
                    {expireSoon && (
                      <div className="flex ui-badge-risk mt-4">
                        <svg
                          className="ui-icon w-4 h-4"
                          focusable="false"
                          aria-hidden="true"
                          role="presentation"
                        >
                          <use href="#ui-icon-circle-solid-exclamation"></use>
                        </svg>{" "}
                        <p className="ui-badge-text sr-only md:not-sr-only">
                          <Trans i18nKey="CustomerManagePayment-manage-payment-card-update-auto-pay-title2"></Trans>
                        </p>
                      </div>
                    )}

                    <div className="block text-right mt-6">
                      <button
                        type="button"
                        className="ui-btn-secondary min-w-[89px] mt-4"
                        aria-label="cancel update credit card expiration date"
                        onClick={() => {
                          setErrorMessage(undefined);
                          setModalState(false);
                          setDisableSave(true);
                        }}
                      >
                        {t("CustomerManagePayment-go-back")}
                      </button>
                      <button
                        id="ID_submitPaymentMethod"
                        className="ui-btn-primary min-w-[159px] mt-4"
                        aria-label="save update credit card expiration date"
                        disabled={disableSave}
                        onClick={() => {
                          onSubmit(getValues("expirationDate"));
                        }}
                      >
                        {t("CustomerManagePayment-update-expiration")}
                      </button>
                    </div>
                  </form>
                </EverlakeLoadingOverlay>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      <PaymentProfileConfirmationModel
        state={openDialog}
        setState={setOpenDialog}
        title={title}
        desc={message}
      />
    </>
  );
};

export default UpdateAutopayCCModal;
